import React from 'react';
import styles from './HTMLTable.module.scss';

interface HTMLTableProps {
  html: string;
}

const hasInlineStyles = (html: string): boolean => {
  // Quick check for any inline style attribute
  if (/style\s*=\s*['"]/i.test(html)) return true;
  // Parse and verify within the table subtree
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return false;
    if (table.hasAttribute('style')) return true;
    return !!table.querySelector('[style]');
  } catch {
    return false;
  }
};

// Parse HTML table into headers/rows, expanding colspan/rowspan
const parseHtmlTable = (html: string): { headers: string[]; rows: string[][] } => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const table = doc.querySelector('table');
  if (!table) return { headers: [], rows: [] };

  const expandRow = (
    row: HTMLTableRowElement,
    colCount: number,
    skipMap: Record<string, boolean>,
  ) => {
    const cells: string[] = [];
    let colIndex = 0;
    row.childNodes.forEach((cellNode) => {
      if (!(cellNode instanceof HTMLTableCellElement)) return;
      while (skipMap[`${colIndex}`]) {
        cells.push('');
        colIndex++;
      }
      const cell = cellNode as HTMLTableCellElement;
      const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
      const rowspan = parseInt(cell.getAttribute('rowspan') || '1', 10);
      for (let i = 0; i < colspan; i++) {
        cells.push(cell.textContent || '');
        if (rowspan > 1) {
          for (let r = 1; r < rowspan; r++) {
            skipMap[`${colIndex + i},${r}`] = true;
          }
        }
      }
      colIndex += colspan;
    });
    while (cells.length < colCount) cells.push('');
    return cells;
  };

  const allRows = Array.from(table.querySelectorAll('tr'));
  let colCount = 0;
  allRows.forEach((row) => {
    let count = 0;
    row.childNodes.forEach((cellNode) => {
      if (!(cellNode instanceof HTMLTableCellElement)) return;
      const colspan = parseInt(cellNode.getAttribute('colspan') || '1', 10);
      count += colspan;
    });
    if (count > colCount) colCount = count;
  });

  let headers: string[] = [];
  const thead = table.querySelector('thead');
  if (thead) {
    const headerRow = thead.querySelector('tr');
    if (headerRow) {
      headers = expandRow(headerRow, colCount, {});
    }
  } else {
    const firstRow = table.querySelector('tr');
    if (firstRow) {
      headers = expandRow(firstRow, colCount, {});
    }
  }

  const rows: string[][] = [];
  let skipMap: Record<string, boolean> = {};
  let rowIndex = 0;
  allRows.forEach((row, i) => {
    if (i === 0 && headers.length) return;
    const rowSkipMap: Record<string, boolean> = {};
    Object.keys(skipMap).forEach((key) => {
      const [col, r] = key.split(',').map(Number);
      if (r === rowIndex) {
        rowSkipMap[`${col}`] = true;
      }
    });
    const cells = expandRow(row, colCount, rowSkipMap);
    if (cells.some((c) => c !== '')) rows.push(cells);
    const newSkipMap: Record<string, boolean> = {};
    Object.keys(skipMap).forEach((key) => {
      const [col, r] = key.split(',').map(Number);
      if (r > rowIndex) {
        newSkipMap[`${col},${r}`] = true;
      }
    });
    row.childNodes.forEach((cellNode, colIdx) => {
      if (!(cellNode instanceof HTMLTableCellElement)) return;
      const colspan = parseInt(cellNode.getAttribute('colspan') || '1', 10);
      const rowspan = parseInt(cellNode.getAttribute('rowspan') || '1', 10);
      if (rowspan > 1) {
        for (let c = 0; c < colspan; c++) {
          for (let r = 1; r < rowspan; r++) {
            newSkipMap[`${colIdx + c},${rowIndex + r}`] = true;
          }
        }
      }
    });
    skipMap = newSkipMap;
    rowIndex++;
  });

  return { headers, rows };
};

const stripEmptyColumns = (headers: string[], rows: string[][]) => {
  const colCount = headers.length;
  const nonEmptyCols: number[] = [];
  for (let i = 0; i < colCount; i++) {
    const headerHasContent = headers[i] && headers[i].trim() !== '';
    const anyRowHasContent = rows.some((row) => row[i] && row[i].trim() !== '');
    if (headerHasContent || anyRowHasContent) {
      nonEmptyCols.push(i);
    }
  }
  const filteredHeaders = nonEmptyCols.map((i) => headers[i]);
  const filteredRows = rows.map((row) => nonEmptyCols.map((i) => row[i]));
  return { headers: filteredHeaders, rows: filteredRows };
};

const getColumnCharWidths = (headers: string[], rows: string[][]) => {
  if (!headers.length) return [] as number[];
  const colCount = headers.length;
  const maxCharCounts = Array(colCount).fill(0) as number[];
  headers.forEach((header, i) => {
    maxCharCounts[i] = Math.max(maxCharCounts[i], (header || '').length);
  });
  rows.forEach((row) => {
    row.forEach((cell, i) => {
      maxCharCounts[i] = Math.max(maxCharCounts[i], (cell || '').length);
    });
  });
  return maxCharCounts;
};

const renderParsedTable = (html: string) => {
  let { headers, rows } = parseHtmlTable(html);
  ({ headers, rows } = stripEmptyColumns(headers, rows));
  if (!headers.length && !rows.length) return null;

  const maxCharCounts = getColumnCharWidths(headers, rows);

  return (
    <div className={styles.tableContainer}>
      <table className={styles.sharedTable}>
        <colgroup>
          {maxCharCounts.map((charCount, i) => (
            <col
              key={i}
              style={{
                minWidth: Math.min(Math.max(charCount * 8, 60), 300),
                maxWidth: 300,
              }}
            />
          ))}
        </colgroup>
        {headers.length > 0 && (
          <thead>
            <tr>
              {headers.map((header, i) => (
                <th key={i} className={styles.th}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => {
                const isNumber = /^-?\d+(\.\d+)?$/.test(cell.trim());
                return (
                  <td key={j} className={isNumber ? styles.tdNumber : styles.tdText}>
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const HTMLTable: React.FC<HTMLTableProps> = ({ html }) => {
  // If the HTML contains style tags or inline styles, render raw to preserve formatting
  if (/<style[\s>]/i.test(html) || hasInlineStyles(html)) {
    return <div className={styles.rawHtmlWrapper} dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return renderParsedTable(html);
};

export default HTMLTable;
